import { z } from 'zod';

import { responseIdSchema } from './common.ts';

const clientExtensionResultsSchema = z.record(z.string(), z.unknown());
export const authenticatorTransportSchema = z.enum([
  'ble',
  'cable',
  'hybrid',
  'internal',
  'nfc',
  'smart-card',
  'usb',
]);
const credentialResponseBase = z
  .object({
    clientExtensionResults: clientExtensionResultsSchema,
    id: z.string().min(1),
    rawId: z.string().min(1),
    type: z.literal('public-key'),
  })
  .passthrough();
export const authenticationResponseSchema = credentialResponseBase.extend({
  response: z
    .object({
      authenticatorData: z.string().min(1),
      clientDataJSON: z.string().min(1),
      signature: z.string().min(1),
      userHandle: z
        .string()
        .nullish()
        .transform((value) => value ?? undefined),
    })
    .passthrough(),
});
export const registrationResponseSchema = credentialResponseBase.extend({
  response: z
    .object({
      attestationObject: z.string().min(1),
      clientDataJSON: z.string().min(1),
      transports: z.array(authenticatorTransportSchema).optional(),
    })
    .passthrough(),
});

export const authFailureCodeSchema = z.enum([
  'conflict',
  'merge-conflict',
  'expired',
  'invalid',
  'unauthorized',
  'authentication',
]);
export const authErrorSchema = z
  .object({ error: authFailureCodeSchema })
  .strict();
export const authOkSchema = z.object({ ok: z.literal(true) }).strict();
export const declinedSchema = z.object({ declined: z.literal(true) }).strict();
export const registrationOptionsSchema = z
  .object({
    challenge: z.string().min(1),
    pubKeyCredParams: z.array(
      z
        .object({ alg: z.number().int(), type: z.literal('public-key') })
        .strict(),
    ),
    rp: z.object({ id: z.string().optional(), name: z.string() }).strict(),
    user: z
      .object({ displayName: z.string(), id: z.string(), name: z.string() })
      .strict(),
  })
  .passthrough();
export const authenticationOptionsSchema = z
  .object({
    allowCredentials: z
      .array(
        z
          .object({
            id: z.string(),
            transports: z.array(authenticatorTransportSchema).optional(),
            type: z.literal('public-key'),
          })
          .strict(),
      )
      .optional(),
    challenge: z.string().min(1),
    rpId: z.string().optional(),
    userVerification: z
      .enum(['discouraged', 'preferred', 'required'])
      .optional(),
  })
  .passthrough();
export const sessionSchema = z
  .object({
    user: z
      .object({
        id: responseIdSchema,
        locale: z.enum(['en', 'ru']),
        name: z.string().nullable(),
        passkeyLinked: z.boolean(),
        telegramLinked: z.boolean(),
      })
      .strict(),
  })
  .strict();
export const tokenRequestSchema = z
  .object({ token: z.string().min(1) })
  .strict();
export const signupOptionsRequestSchema = z
  .object({ locale: z.string().optional() })
  .strict();
export const challengeResponseSchema = z
  .object({
    challenge: z.string().min(1),
    response: authenticationResponseSchema,
  })
  .strict();
export const registrationChallengeResponseSchema = z
  .object({
    challenge: z.string().min(1),
    response: registrationResponseSchema,
  })
  .strict();
export const mergeVerifyResponseSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('same-user'), userId: responseIdSchema }).strict(),
  z
    .object({
      kind: z.literal('confirmation'),
      token: z.string().min(1),
      userId: responseIdSchema,
    })
    .strict(),
]);
export type Session = z.infer<typeof sessionSchema>;
export type AuthFailureCode = z.infer<typeof authFailureCodeSchema>;
